import { Box, Button, Card, CardContent, Chip, CircularProgress, Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react'
import Loading from '../../../../components/Loader/Loading';
import { DataGrid } from '@mui/x-data-grid';
import CustomPagination from '../../../../components/pagination/CustomPagination';
import { useForm, Controller } from 'react-hook-form';
import { FormControl, InputAdornment, IconButton, MenuItem, Select, TextField } from '@mui/material';
import { ArrowBack, Clear, Download, Search } from '@mui/icons-material';
import dayjs from 'dayjs';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ExportAll from './ExportAll'
import { useDispatch } from 'react-redux';
import PageHeaderLayout from '../../../../components/Layout/PageHeaderLayout';
import { TaxService } from '../TaxService';
import { CuDelReceipt } from './pdf/ExpertPlatformPdf';
import { pdf } from '@react-pdf/renderer';
import CudelLogo from '../../../../assets/images/cudel_logo.png';
import AuthorizedSignature from '../../../../assets/images/authorized_signature.png';
import Swal from 'sweetalert2';
import { createZipBlob } from '../../../../utils/createZipBlob';

const getLastWeekRange = () => {
    const today = dayjs();
    const daysToThisMonday = (today.day() + 6) % 7;
    const thisMonday = today.subtract(daysToThisMonday, 'day');
    const lastMonday = thisMonday.subtract(7, 'day');
    const lastSunday = thisMonday.subtract(1, 'day');
    
    return {
        startDate: lastMonday.format('YYYY-MM-DD'),
        endDate: lastSunday.format('YYYY-MM-DD'),
    };
};

const PlatformTaxDetail = (props) => {
    const { } = props;
    const menuDispatch = useDispatch();
    useEffect(() => {
        menuDispatch({
            type: 'menuSelect',
            payload: 'tax-report'
        });
    }, []);

    const {control,watch,handleSubmit,setValue, formState: { isDirty }} = useForm({
        defaultValues: getLastWeekRange()
    });
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [finalPayout, setFinalPayout] = useState(null);
    const [zipLoading, setZipLoading] = useState(false);

    const sanitizeFileName = (value) => String(value || 'invoice')
        .replace(/[\\/:*?"<>|]+/g, '-')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();

    const downloadBlob = (blob, fileName) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    const loadPdfAssets = async () => {
        return await Promise.all([
            fetch(CudelLogo).then(r => r.blob()).then(b => new Promise((res, rej) => { const rd = new FileReader(); rd.onloadend = () => res(rd.result); rd.onerror = rej; rd.readAsDataURL(b); })),
            fetch(AuthorizedSignature).then(r => r.blob()).then(b => new Promise((res, rej) => { const rd = new FileReader(); rd.onloadend = () => res(rd.result); rd.onerror = rej; rd.readAsDataURL(b); })),
        ]);
    };

    const handleDownloadZipPdfs = async () => {
        if (!rows.length) {
            Swal.fire({
                title: 'No PDFs Found',
                text: 'There are no expert platform invoices in the current list.',
                icon: 'info',
                confirmButtonText: 'OK',
            });
            return;
        }

        const result = await Swal.fire({
            title: `Export ${rows.length} PDF${rows.length > 1 ? 's' : ''} as ZIP?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Export ZIP',
            cancelButtonText: 'Cancel',
            allowOutsideClick: false,
        });

        if (!result.isConfirmed) return;

        setZipLoading(true);
        Swal.fire({
            title: 'Preparing PDF ZIP...',
            html: `<p id="platform-pdf-download-progress">Downloading PDF 0 of ${rows.length}</p>`,
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => Swal.showLoading(),
        });

        const files = [];
        const failedInvoices = [];

        try {
            const [logoBase64, signatureBase64] = await loadPdfAssets();

            for (let index = 0; index < rows.length; index += 1) {
                const row = rows[index];
                const progressEl = Swal.getHtmlContainer()?.querySelector('#platform-pdf-download-progress');
                if (progressEl) {
                    progressEl.textContent = `Downloading PDF ${index + 1} of ${rows.length}`;
                }

                try {
                    const blob = await pdf(
                        <CuDelReceipt data={row} logoBase64={logoBase64} signatureBase64={signatureBase64} />
                    ).toBlob();
                    const arrayBuffer = await blob.arrayBuffer();
                    files.push({
                        name: `Expert-Platform-Tax-Receipt-${sanitizeFileName(row.invoiceNo)}.pdf`,
                        bytes: new Uint8Array(arrayBuffer),
                    });
                } catch (error) {
                    console.error(`Failed to generate platform invoice ${row.invoiceNo}`, error);
                    failedInvoices.push(row.invoiceNo);
                }
            }
        } catch (error) {
            console.error('Platform invoice ZIP download failed', error);
            setZipLoading(false);
            Swal.fire({
                title: 'Export Failed',
                text: 'Unable to prepare expert platform invoice PDFs.',
                icon: 'error',
                confirmButtonText: 'OK',
            });
            return;
        }

        setZipLoading(false);

        if (!files.length) {
            Swal.fire({
                title: 'Export Failed',
                text: 'Unable to prepare any expert platform invoice PDFs.',
                icon: 'error',
                confirmButtonText: 'OK',
            });
            return;
        }

        downloadBlob(createZipBlob(files), `expert-platform-invoices-${dayjs(startDate).format('DD-MM-YYYY')}_to_${dayjs(endDate).format('DD-MM-YYYY')}.zip`);
        Swal.fire({
            title: failedInvoices.length ? 'ZIP Downloaded with Some Missing PDFs' : 'ZIP Downloaded',
            text: failedInvoices.length
                ? `${files.length} PDF(s) exported. ${failedInvoices.length} PDF(s) could not be added.`
                : `${files.length} PDF(s) exported successfully.`,
            icon: failedInvoices.length ? 'warning' : 'success',
            confirmButtonText: 'Close',
        });
    };

    const handleInvoiceClick = async (rowData) => {
        const result = await Swal.fire({
            title: 'Generate Invoice',
            text: `Do you want to generate invoice for ${rowData.invoiceNo}?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Generate',
            cancelButtonText: 'Cancel',
            allowOutsideClick: false,
        });
        if (!result.isConfirmed) return;

        Swal.fire({
            title: 'Generating PDF',
            text: 'Please wait while the PDF is being generated...',
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => Swal.showLoading(),
        });

        try {
            const [logoBase64, signatureBase64] = await loadPdfAssets();
            const blob = await pdf(
                <CuDelReceipt data={rowData} logoBase64={logoBase64} signatureBase64={signatureBase64} />
            ).toBlob();
            Swal.close();
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, '_blank');
        } catch (error) {
            console.error('Error generating PDF:', error);
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to generate PDF. Please try again.' });
        }
    };

    const columns=[
        { field: 'invoiceNo', sortable:false ,headerName: 'Invoice No', width: 180, headerAlign: 'left', align: 'left',
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Invoice No</span>,
            renderCell: (params) =>(
                <Typography color="primary" sx={{fontSize:'.65rem',cursor:'pointer'}} onClick={() => handleInvoiceClick(params.row)}>

                    {params.value}
                    {/* {' '} */}
                    {/* {params.row.customerId == params.row.createdBy && <PhoneAndroid sx={{fontSize:'.65rem'}} color='warning'/>} */}
                </Typography>
            )
        },
        
        {
            field: 'invoiceType', sortable:false ,headerName: 'Invoice Type', width: 150, headerAlign: 'left', align: 'left',
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Invoice Type</span>,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', width:'100%' }}>
                    <Typography variant="body2" sx={{textTransform:'capitalize',fontSize:'.75rem'}}>{params.value}</Typography>
                </Box>
            )
        },
        {
            field: 'payoutDate', sortable:false ,headerName: 'Payout Date', width: 150, headerAlign: 'left', align: 'left',
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Payout Date</span>,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', width:'100%' }}>
                    <Typography variant="body2" sx={{textTransform:'capitalize',fontSize:'.75rem'}}>{params.value}</Typography>
                </Box>
            )
        },
        {
            field: 'expertName', sortable:false ,headerName: 'Expert Name', width: 120, headerAlign: 'left', align: 'left',
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Expert Name</span>,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', width:'100%' }}>
                    <Typography variant="body2" sx={{textTransform:'capitalize',fontSize:'.75rem'}}>{params.value}</Typography>
                </Box>
            )

        },
        {
            field: 'gstin', sortable:false ,headerName: 'GSTIN (if any)', width: 120, headerAlign: 'left', align: 'left',
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>GSTIN (if any)</span>,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', width:'100%' }}>
                    <Typography variant="body2" sx={{textTransform:'capitalize',fontSize:'.75rem'}}>{params.value}</Typography>
                </Box>
            )

        },
        {
            field: 'placeOfSupply', sortable:false ,headerName: 'Place of Supply', width: 120, headerAlign: 'left', align: 'left',
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Place of Supply</span>,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', width:'100%' }}>
                    <Typography variant="body2" sx={{textTransform:'capitalize',fontSize:'.75rem'}}>{params.value}</Typography>
                </Box>
            )

        },
        {
            field: 'taxableValue', sortable:false ,headerName: 'Taxable Value', width: 120, headerAlign: 'left', align: 'left',
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Taxable Value</span>,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', width:'100%' }}>
                    <Typography variant="body2" sx={{textTransform:'capitalize',fontSize:'.75rem'}}>₹{parseFloat(params.value).toFixed(2)}</Typography>
                </Box>
            )

        },
        {
            field: 'gstRate', sortable:false ,headerName: 'GST Rate', width: 120, headerAlign: 'left', align: 'left',
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>GST Rate</span>,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', width:'100%' }}>
                    <Typography variant="body2" sx={{textTransform:'capitalize',fontSize:'.75rem'}}>{params.value}%</Typography>
                </Box>
            )

        },
        {
            field: 'cgst', sortable:false ,headerName: 'CGST', width: 120, headerAlign: 'left', align: 'left',
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>CGST</span>,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', width:'100%' }}>
                    <Typography variant="body2" sx={{textTransform:'capitalize',fontSize:'.75rem'}}>₹{parseFloat(params.value).toFixed(2)}</Typography>
                </Box>
            )

        },
        { field: 'sgst', sortable:false ,headerName: 'SGST', width: 150, headerAlign: 'left', align: 'left',
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>SGST</span>,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', width:'100%' }}>
                    <Typography variant="body2" sx={{textTransform:'capitalize',fontSize:'.75rem'}}>₹{parseFloat(params.value).toFixed(2)}</Typography>
                </Box>
            )
        },
        { field: 'igst', sortable:false ,headerName: 'IGST', width: 120, headerAlign: 'left', align: 'left',
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>IGST</span> ,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', width:'100%' }}>
                    <Typography variant="body2" sx={{textTransform:'capitalize',fontSize:'.75rem'}}>₹{parseFloat(params.value).toFixed(2)}</Typography>
                </Box>
            )
        },
        { field: 'totalInvoiceValue', sortable:false ,headerName: 'Total Invoice Value', width: 110, headerAlign: 'left', align: 'left',
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Total Invoice Value</span>,
            // valueFormatter: (params) => ,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', width:'100%' }}>
                    <Typography variant="body2" sx={{textTransform:'capitalize',fontSize:'.75rem'}}>₹{parseFloat(params.value).toFixed(2)}</Typography>
                </Box>
            )
        },
        { field: 'serviceType', sortable:false ,headerName: 'Service Type', width: 160, headerAlign: 'left', align: 'left',
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Service Type</span> ,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', width:'100%' }}>
                    <Typography variant="body2" sx={{textTransform:'capitalize',fontSize:'.75rem'}}>{params.value}</Typography>
                </Box>
            )
        },
        { field: 'paymentMode', sortable:false ,headerName: 'Payment Mode', width: 60, headerAlign: 'center', align: 'right',
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Payment Mode</span> ,
            renderCell: (params) => (
                <Box sx={{ display: 'flex',alignItems:"center",justifyContent:'center', width:'100%' }}>
                    <Typography variant="body2" sx={{textTransform:'capitalize',fontSize:'.75rem'}}>{params.value}</Typography>
                </Box>
            )
        },
        { field: 'Remarks', sortable:false ,headerName: 'Remarks', width: 80, headerAlign: 'center', align: 'right',
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Remarks</span>,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems:"center",justifyContent:'center', width:'100%' }}>
                    <Typography variant="body2" sx={{textTransform:'capitalize',fontSize:'.75rem'}}>{params.value}</Typography>
                </Box>
            ) 
        }
    ]
    
    const startDate = watch('startDate');
    const endDate = watch('endDate');
    const handleClear = () => {
        const { startDate: defaultStart, endDate: defaultEnd } = getLastWeekRange();
        setValue('startDate', defaultStart);
        setValue('endDate', defaultEnd);
        setRows([]);
        setFinalPayout(null);
        fetchPayoutRecords();
    }
    const submitHandler = (data) => {
        // fetchPayoutRecords();
        console.log(data);
        fetchPayoutRecords();
    }
    const fetchPayoutRecords = async () => {
        console.log('Fetching payout records with:', { startDate, endDate });
        // return;
        setLoading(true);
        setRows([]);
        setFinalPayout(null);

        try {
            let response;
            if (startDate && endDate) {
                response = await TaxService.getExpertPlatformTaxDetails(
                    startDate,
                    endDate
                );
            }

            if (response?.success === 1) {
                if (response.data.rows) {
                    setRows(response.data.rows);
                }
                if (response.data.payout_records) {
                    setFinalPayout(response.data.payout_records);
                }
            }
        } catch (error) {
            console.error('Error fetching tax data:', error);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchPayoutRecords();
    }, []);
  return (
    <>
        <PageHeaderLayout headerTitle="Expert Payout Report" >
            <Button variant='contained' onClick={() => window.history.back()} size='small' endIcon={<ArrowBack />}>
                Back
            </Button>
        </PageHeaderLayout>
        <Card>
            <Box sx={{ overflow: "auto" }}>
                <Box 
                    sx={{ 
                    width: "100%", display: "table",  tableLayout: "fixed", 
                    backgroundColor: "#fff", borderRadius: '.5rem',
                    '& .odd': {
                        bgcolor: 'white',
                    },
                    '& .even': {
                        bgcolor: '#ECECEC',
                    },
                }}>
    
            {loading && <Loading/>}
                <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between',alignItems:'center',p:'1%' }}>
                
                    <Box component={'form'} onSubmit={handleSubmit(submitHandler)} flex={1}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={3}>
                                <Controller
                                    name="startDate"
                                    control={control}
                                    rules={{
                                        // validate: (value) => {
                                        // if (!value) return "This field is required";
                                        // const date = dayjs(value);
                                        // const today = dayjs().startOf('day');
                                        // return date.isValid() && (date.isSame(today, 'day') || date.isAfter(today)) || "Booking Date must be today or in the future";
                                        // }
                                    }}
                                    render={({ field: { onChange, value, ...restField }, fieldState: { error } }) => (
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DesktopDatePicker
                                                {...restField}
                                                sx={{
                                                '& . iconButton': {
                                                    padding: 0.5,
                                                    fontSize: '1rem'
                                                }
                                                }}
                                                label="Start Date"
                                                // disabled={!watch('services').length>0 || !watch('customer')}
                                                value={value ? dayjs(value) : null}
                                                onChange={(newValue) => {
                                                // Store the date in YYYY-MM-DD format for database compatibility
                                                onChange(newValue ? newValue.format("YYYY-MM-DD") : null);
                                                }}
                                                format="DD-MM-YYYY" // Display format for user
                                                // minDate={dayjs()}
                                                maxDate={dayjs(watch('endDate'))}
                                                slotProps={{
                                                textField: {
                                                    error: !!error,
                                                    helperText: error?.message,
                                                    fullWidth: true,
                                                    required: true,
                                                    size: 'small',
                                                    inputProps: {
                                                    style: { fontSize: '.75rem' }
                                                    }
                                                }
                                                }}
                                            />
                                        </LocalizationProvider>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                {/* <DatePicker
                                    label="End Date"
                                    value={endDate}
                                    onChange={(newValue) => setEndDate(newValue)}
                                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                    minDate={startDate}
                                    maxDate={dayjs()}
                                /> */}
                                <Controller
                                    name="endDate"
                                    control={control}
                                    rules={{
                                        // validate: (value) => {
                                        // if (!value) return "This field is required";
                                        // const date = dayjs(value);
                                        // const today = dayjs().startOf('day');
                                        // const start = dayjs(watch('startDate'));
                                        // if (!date.isValid()) return "Invalid date";
                                        // if (date.isBefore(start)) return "End Date cannot be before Start Date";
                                        // // if (date.isAfter(today)) return "End Date cannot be in the future";
                                        // return true;
                                        // }
                                    }}
                                    render={({ field: { onChange, value, ...restField }, fieldState: { error } }) => (
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DesktopDatePicker
                                                {...restField}
                                                sx={{
                                                '& . iconButton': {
                                                    padding: 0.5,
                                                    fontSize: '1rem'
                                                }
                                                }}
                                                label="End Date"
                                                // disabled={!watch('services').length>0 || !watch('customer')}
                                                value={value ? dayjs(value) : null}
                                                onChange={(newValue) => {
                                                // Store the date in YYYY-MM-DD format for database compatibility
                                                onChange(newValue ? newValue.format("YYYY-MM-DD") : null);
                                                }}
                                                format="DD-MM-YYYY" // Display format for user
                                                minDate={dayjs(watch('startDate'))}
                                                // maxDate={dayjs()}
                                                slotProps={{
                                                textField: {
                                                    error: !!error,
                                                    helperText: error?.message,
                                                    fullWidth: true,
                                                    required: true,
                                                    size: 'small',
                                                    inputProps: {
                                                    style: { fontSize: '.75rem' }
                                                    }
                                                }
                                                }}
                                            />
                                        </LocalizationProvider>
                                    )}
                                />
                            </Grid>                           
                            
                            <Grid item xs={12} sm={3} sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant="contained"
                                    type='submit'
                                    // onClick={fetchPayoutRecords}
                                    disabled={loading || !isDirty}
                                    startIcon={<Search />}
                                    size="small"
                                >
                                    {loading ? <CircularProgress size={20} color="inherit" /> : 'Search'}
                                </Button>
                                {/* {(searchType === 'booking' || rows.length > 0) && ( */}
                                    <IconButton onClick={handleClear} size="small" title="Clear">
                                        <Clear />
                                    </IconButton>
                                {/* )} */}
                                
                            </Grid>
                        </Grid>
                        

                        
                        {/* <TextField
                        value={quickFilterText}
                        onChange={(event) => setQuickFilterText(event.target.value)}
                        placeholder="Quick Search ..."
                        size="small"
                        fullWidth
                        sx={{
                            '& .MuiOutlinedInput-root': {
                            paddingLeft: '0px',
                            }
                        }}
                        InputProps={{
                            inputProps: {
                            style: {
                                height: '0.6rem',
                                textTransform: 'uppercase',
                                fontSize: 'x-small'
                            },
                            },
                            startAdornment: (
                            <InputAdornment position="start">
                                <IconButton>
                                <Search sx={{fontSize:'x-small'}}/>
                                </IconButton>
                            </InputAdornment>
                            ),
                        }}
                        /> */}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {rows.length > 0 && (
                        <>
                        <Button
                            onClick={handleDownloadZipPdfs}
                            disabled={zipLoading}
                            startIcon={zipLoading ? <CircularProgress size={16} color="inherit" /> : <Download />}
                            sx={{ textTransform: 'none' }}
                        >
                            {zipLoading ? 'Preparing PDFs...' : 'Download PDFs'}
                        </Button>
                        <ExportAll taxData = {rows} startDate={startDate} endDate={endDate} />
                        </>

                            // <Button
                            //     variant="outlined"
                            //     onClick={exportToExcel}
                            //     startIcon={<Download />}
                            //     size="small"
                            // >
                            //     Export
                            // </Button>
                        )}
                    </Box>
                </Box>
                <DataGrid
                    rows={rows.map((row, index) => ({ ...row, id: `${row?.bookingNumber}-${index}` }))}
                    columns={columns.map((col) => ({
                        ...col,
                        flex: col.flex || 1, // Ensure columns resize dynamically
                        minWidth: col.minWidth || 150, // Set a minimum width to avoid overlap
                    }))}
                    getRowId={(row) => row?.id}
                    rowCount={rows && rows.length > 0 ? rows.length : 0} // Use totalRecords from API response
                    pageSizeOptions={[5, 10, 20, 50, 100]}
                    
                    showFirstButton={true}
                    showLastButton={true}

                    disableMultiSort={true}
        
                    loading={loading}
                    disableColumnMenu
                    disableRowSelectionOnClick
                    disableDensitySelector
                    sortingOrder={['asc', 'desc']}
                    slots={{
                        // toolbar: GridToolbar,
                        loadingOverlay: Loading,
                        // Optionally use custom pagination
                        pagination: CustomPagination,
                    }}
                    autoHeight
                    getRowHeight={() => 'auto'}
                    sx={{
                 minWidth: "100%", // Ensures DataGrid expands inside scrollable container
                        "& .MuiDataGrid-root": {
                        overflowX: "auto",
                        },
                  height:'68vh',
                    borderRadius:'0',
                    '& .MuiDataGrid-cell': {
                    border: 1,
                    borderRight: 0,
                    borderTop: 0,
                    borderColor: 'lightgrey',
                    display: 'flex',
                    alignItems: 'center',
                    // justifyContent: 'center',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                        borderBottom: '2px solid rgba(224, 224, 224, 1)',
                    //   borderTop: '2px solid rgba(224, 224, 224, 1)',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        p:0,
                        minHeight:'0 !important',
                        height:'2rem'
                    },
                    '& .MuiDataGrid-columnHeader': {
                    borderLeft: '1px solid rgba(224, 224, 224, 1)',
                    borderBottom: 'none',
                    '&:first-child': {
                        borderLeft: 'none',
                    },
                    '&:last-child': {
                        borderRight: 'none',
                    },
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 600,
                    color: '#3e3e3e'
                    },
                    '& .MuiDataGrid-row': {
                        '& .MuiDataGrid-cell': {
                            py:1
                        },
                        // Add styles for deleted rows
                        '&.deleted-row': {
                            backgroundColor: '#ffebee !important', // Light red background
                            '&:hover': {
                            backgroundColor: '#ffcdd2 !important', // Darker red on hover
                            }
                        },
                        '& .MuiDataGrid-cell:first-child': {
                        borderLeft: 'none',
                    },
                    '&:last-child .MuiDataGrid-cell': {
                        borderBottom: '1px solid rgba(224, 224, 224, 1)',
                    },
                    '& .MuiDataGrid-cell:last-child': {
                        borderRight: 'none',
                    },
                    },
                    '& .MuiDataGrid-columnSeparator': {
                    display: 'none',
                    },
                }}
                    />
                </Box>
            </Box>
        </Card>

    </>
  )
}

export default PlatformTaxDetail
